import Tastet from '../tastet.js';
import Template from '../modules/template.js';
import GoogleMap from '../modules/google-map.js';
import Modal from './modal.js';
import { default as utils } from '../core/utils.js';

var GOOGLE_MAP_STYLE = [
  {
    featureType: 'all',
    elementType: 'labels.text.fill',
    stylers: [{ saturation: 36 }, { color: '#333333' }, { lightness: 40 }],
  },
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [{ visibility: 'on' }, { color: '#ffffff' }, { lightness: 16 }],
  },
  {
    featureType: 'all',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [{ color: '#fefefe' }, { lightness: 20 }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }, { lightness: 20 }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }, { lightness: 21 }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#ccf1ab' }, { lightness: 21 }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }, { lightness: 16 }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#f2f2f2' }, { lightness: 19 }],
  },
  {
    featureType: 'transit.line',
    elementType: 'all',
    stylers: [{ visibility: 'simplified' }],
  },
  {
    featureType: 'transit.station.airport',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#5fe3f3' }, { lightness: '76' }, { saturation: '-31' }],
  },
];

class Marker {
  // init --------------------------------------------------------------------
  constructor(id, data, map, parent) {
    data = data || {};

    this.id = id;
    this.map = map;
    this.position = new google.maps.LatLng(data.lat, data.lng);
    this._data = data;
    this._parent = parent;
    this._isOpen = false;
    this._isLiked = !!this._data.isLiked;
    this._isCoco = !!this._data.isCoco;
    this._link = this._data.link;
    this._isVisible = false;

    this.marker = new google.maps.Marker({
      id: id,
      position: this.position,
      zIndex: 10,
    });

    this.isVisible(true);
  }

  // private -----------------------------------------------------------------
  _icon() {
    var icon = (this._isLiked ? 'like' : 'marker') + (this._isOpen ? '_on' : '');
    if (this._isCoco) icon = 'coco';
    var url = this._data[icon] || this._parent.data['icon_' + icon] || icon + '.png';
    var size = this._isLiked ? [20, 19] : this._parent.get('iconSize') || [25, 20];
    if (this._isCoco) size = [20, 25];

    this.marker.setIcon({
      url: Tastet.image_url + url,
      scaledSize: new google.maps.Size(size[0], size[1]),
    });
  }

  // getter/setter -----------------------------------------------------------
  inView() {
    return this._parent._inView(this.marker);
  }

  isUnlocked() {
    return this.inView();
  }

  isVisible(v) {
    if (utils.is.bool(v)) {
      if (v) {
        this.marker.setMap(this.map);
        this._icon();
      } else {
        this.marker.setMap(null);
      }
      this._isVisible = v;
      return this;
    }
    return this._isVisible;
  }

  isLiked(v) {
    if (utils.is.bool(v)) {
      this._isLiked = v;
      this._icon();
      return this;
    }
    return this._isLiked;
  }

  isOpen(v) {
    if (utils.is.bool(v)) {
      this._isOpen = v;

      if (v) {
        this.marker.setZIndex(100);
      } else {
        this.marker.setZIndex(10);
      }

      this._icon();
      return this;
    }
    return this._isOpen;
  }

  get(key) {
    var cache = (this._cache = this._cache || {});
    if (key && key in cache) {
      return cache[key];
    }

    var value = this._data[key];
    if (utils.is.encoded(value) && this.isUnlocked()) {
      value = cache[key] = utils.decode(value);
    }

    return value;
  }
}

export default class Map extends Tastet.View {
  preload(done) {
    new GoogleMap(done);
  }

  // init ------------------------------------------------------------------------------------------------------------
  init($el, data) {
    data = this.set(data || {}, {
      width: '100%',
      height: '70vh',
      zoom: 14,
      centerLat: Tastet.settings.map_center.lat,
      centerLng: Tastet.settings.map_center.lng,
      centerMarkers: true, // center to the bounds of the markers
      interactive: true,
      iconSize: '20,25',
      icon_marker: 'marker.svg',
      icon_marker_on: 'marker_on.svg',
      icon_like: 'like.svg',
      icon_like_on: 'like_on.svg',
      icon_coco: 'coco.svg',
      icon_coco_on: 'coco_on.svg',
    });
    data.iconSize = this.utils.toValues(data.iconSize);

    this.template = new Template($el);
    this.items = [];

    // extract all the items
    var $items = this.$element.children().remove();

    // check if the content (innerHTML might be the markers)
    var html = this.$element.html().trim();
    var items = this.utils.decode(html) || [];

    // create the map
    this.$element.style({
      width: data.width,
      height: data.height,
    });
    this._map();

    // add markers
    $items.each(true, function (el) {
      var item = el.data();
      item.html = el.html();
      this.add(item);
    });

    this.each(items, function (item) {
      this.add(item);
    });

    // add modal for info
    this.$modal = this.$('<div>').appendTo(this.$element);
    this.modal = new Modal(this.$modal, { alias: 'map-info' });

    if (this.data.centerMarkers) {
      this.centerMarkers();
    }

    window.map = this;
  }

  addEvents() {
    google.maps.event.addListener(this.map, 'bounds_changed', () => {
      if (this.get('isMapReady')) return;
      this.set('isMapReady', true);
      this.when('map-ready', true);
    });

    google.maps.event.addListener(this.map, 'idle', () => this.map_onIdle());
    google.maps.event.addListener(this.map, 'dragend', () => this.map_onDragEnd());
    google.maps.event.addListener(this.map, 'idle', () => this.map_onBoundsChange());

    window.googleMaps = google.maps;
    window.markerParent = this;
    dispatchEvent(new Event('googleMapReady'));
  }

  // private  --------------------------------------------------------------------------------------------------------
  _map() {
    if (!this.$element.length) {
      return this.error('The map needs an HtmlElement to work');
    }

    var $el = this.$element;
    var data = this.data;

    this.map = new google.maps.Map($el[0], {
      zoom: data.zoom,
      center: new google.maps.LatLng(data.centerLat, data.centerLng),
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      gestureHandling: 'greedy',
      zoomControl: true,
      fullscreenControl: false,
      streetViewControl: false,
      styles: GOOGLE_MAP_STYLE,
    });
  }

  _showInfo(item) {
    if (this.template.empty) return;

    var values = item.get.bind(item);
    var html = this.template.html(values);

    this.$modal.html(html);
    this.modal.open();

    item.isOpen(true);
  }

  _resetMarkerIcons() {
    this.each(this.items, function (item) {
      item.isOpen(false);
    });
  }

  _inView(marker) {
    if (!this.get('isMapReady')) return false;

    var corners = this._mapCorners();
    var lat = marker.position.lat();
    var lng = marker.position.lng();
    var inView = lat >= corners.lat[0] && lat <= corners.lat[1] && lng >= corners.lng[0] && lng <= corners.lng[1];

    return inView;
  }

  _mapCorners(refresh) {
    var corners = this.get('mapCorners', null);

    if (refresh || !corners) {
      var bounds = this.map.getBounds();
      var sw = bounds.getSouthWest();
      var ne = bounds.getNorthEast();
      var lat = [sw.lat(), ne.lat()];
      var lng = [sw.lng(), ne.lng()];

      corners = {
        lng: lng,
        lat: lat,
      };

      this.set('mapCorners', corners);
    }

    return corners;
  }

  // events ----------------------------------------------------------------------------------------------------------
  map_onIdle() {
    this.set('mapCorners', null);
  }

  map_onDragEnd() {
    this.trigger('drag-end');
  }

  map_onBoundsChange() {
    this.trigger('change');
  }

  // methods ---------------------------------------------------------------------------------------------------------
  add(data) {
    data = data || {};

    if (!('lat' in data && 'lng' in data)) {
      return this.error('Can\'t add a Marker without a "lng" and "lat" properties');
    }

    var item = new Marker(data.id, data, this.map, this);

    this.items.push(item);

    this.data.interactive &&
      google.maps.event.addListener(item.marker, 'click', () => {
        this._resetMarkerIcons();

        console.log('item', item);
        if (item._isCoco) {
          window.open(item._link, '_blank').focus();
        } else {
          this._showInfo(item);
          this.trigger('click-marker', { item: item });
        }
      });
  }

  geolocalize(zoom, el) {
    if (navigator.geolocation) {
      this.isLoading(true, el);

      navigator.geolocation.getCurrentPosition(
        pos => {
          this.map.setCenter({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });

          this.map.setZoom(zoom || this.data.zoom);

          this.isLoading(false, el);
        },
        () => {
          this.isLoading(false, el);
        }
      );
    } else {
      // no geolocalization
    }
  }

  centerMarkers(zoom) {
    // vars
    var bounds = new google.maps.LatLngBounds();

    // loop through all items and create bounds
    this.each(this.items, function (item) {
      bounds.extend(item.position);
    });

    if (this.items.length == 1) {
      // set center of map
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(zoom || this.data.zoom);
    } else if (this.items.length) {
      // fit to bounds
      this.map.fitBounds(bounds);
    }
  }
}
